/**
 * 结算明细 —— 把和牌的役种/符数/点数整理成可展示的数据
 *
 * 用于 UI 的结算画面（教学向：让玩家看到"为什么是这样算的"）
 */

import { tileIndex, indexToName, type Tile } from '../tile.js';
import { decomposeAll, type Decomposition } from '../agari.js';
import { detectYaku, type YakuResult } from '../yaku/index.js';
import { calculateFu, type FuResult, type FuWaitType } from '../fu.js';
import { scoreFrom, type ScoreResult } from '../score.js';
import { dealerSeat, seatToWind } from './state.js';
import { rulesOf, doubleYakumanMultiplier } from './rule-config.js';
import { handToCounts } from './rules.js';
import { mentsuTiles } from '../yaku/checkers.js';
import type { WinContext, Yaku } from '../yaku/types.js';
import type { GameState, Seat } from './state.js';

// ════════════════════════════════════════════════════════════════
// 类型
// ════════════════════════════════════════════════════════════════

/** 一条役的信息（UI 展示用）*/
export interface YakuDisplay {
  /** 役种名（中文）*/
  readonly name: string;
  /** 番数 */
  readonly han: number;
  /** 是否为役满 */
  readonly yakuman: boolean;
}

/** 结算明细 */
export interface WinDetail {
  /** 和牌方式 */
  readonly method: 'tsumo' | 'ron';
  /** 和牌者座位 */
  readonly winner: Seat;
  /** 放铳者（荣和时）*/
  readonly loser: Seat | null;
  /** 和了牌 */
  readonly winTile: string;
  /** 和了牌索引（供 UI 渲染牌图）*/
  readonly winTileIndex: number;
  /** 手牌（展示用）*/
  readonly hand: readonly string[];
  /** 副露（展示用）*/
  readonly melds: readonly string[];
  /** 门前手牌（不含和了牌）—— 牌索引，供 UI 渲染真实牌图（而非文字）*/
  readonly handTiles: readonly number[];
  /** 副露（与 UI 的 MeldDisplay 兼容：kind/start/open/calledIndex）*/
  readonly meldGroups: readonly {
    readonly kind: string;
    readonly start: number;
    readonly open: boolean;
    readonly calledIndex: number;
  }[];
  /** 役种列表 */
  readonly yaku: readonly YakuDisplay[];
  /** 宝牌番数 */
  readonly doraHan: number;
  /** 总番数 */
  readonly totalHan: number;
  /** 符数 */
  readonly fu: number;
  /** 符数明细 */
  readonly fuDetail: string;
  /** 点数结果 */
  readonly score: ScoreResult;
  /** 点数变化 */
  readonly scoreChanges: readonly number[];
  /** 是否满贯以上 */
  readonly limitName: string | null;
  /** 是否役满 */
  readonly yakuman: boolean;
  /** 役满倍数（1=单倍役满，2=双倍…）*/
  readonly yakumanCount: number;
}

// ════════════════════════════════════════════════════════════════
// 主函数
// ════════════════════════════════════════════════════════════════

/**
 * 计算和牌明细
 *
 * 流程：
 *   1. 分解手牌（取所有分解，按【点数】取最优 —— 高点法）
 *   2. 判定役种
 *   3. 算符数
 *   4. 算点数
 */
export function computeWinDetail(
  state: GameState,
  winner: Seat,
  method: 'tsumo' | 'ron',
  loser: Seat | null,
  winTile: Tile,
): WinDetail | null {
  const player = state.players[winner];

  // 荣和时，和了牌不在手牌中 → 虚拟加入
  const effectiveHand =
    method === 'ron' ? [...player.hand, winTile] : [...player.hand];
  const counts = handToCounts(effectiveHand);

  // ⚠️ allTiles 必须含副露的牌：counts 只有门前手牌，player.melds 的牌不在里面。
  //    否则「看全牌」的役（清老头/字一色/断幺/混一/清一/混老头/绿一色）鸣牌后会被误判。
  //    踩过：吃 123m 的手被判成清老头（副露里的 2m/3m 没进 allTiles）。
  const allTiles = [...counts];
  for (const m of player.melds) {
    for (const t of mentsuTiles(m)) allTiles[t]++;
  }

  const ds = decomposeAll(counts, player.melds);
  if (ds.length === 0) return null;

  const winCtx: WinContext = {
    roundWind: state.round.roundWind,
    seatWind: seatToWind(winner, state.round),
    winMethod: method,
    isRiichi: player.riichi,
    dora: { doraIndicators: state.wall.doraIndicators.map(tileIndex) },
    winTile: tileIndex(winTile),
    honba: state.round.honba,
    riichiTurn: player.riichiTurn ?? undefined,
  };

  const isClosed = player.melds.filter((m) => m.open).length === 0;

  // ── 遍历所有分解 × 待ち读法，选点数最高的 ──
  let best: {
    yakuResult: YakuResult;
    fuResult: FuResult;
    score: ScoreResult;
    decomp: Decomposition;
  } | null = null;

  const wIdx = tileIndex(winTile);

  for (const d of ds) {
    // 待ち读法候选：同一分解下和了牌可落在不同面子/雀头上，天凤/riichienv 按【点数】取高者（高点法）。
    const readings: Array<FuWaitType | undefined> = waitReadingsFor(d, wIdx);

    for (const reading of readings) {
      try {
        const wc = reading ? { ...winCtx, waitReading: reading } : winCtx;
        const yakuResult = detectYaku({
          decomposition: d,
          counts,
          allTiles,
          meldCount: player.melds.length,
          isClosed,
          winTile: wIdx,
          context: wc,
        });

        // 无役的分解不合法（宝牌不算役）→ 跳过
        if (yakuResult.yaku.length === 0) continue;

        const isPinfu = yakuResult.yaku.some((y) => y.def.id === 'pinfu');
        const fuResult = calculateFu(d, wc, isPinfu, true);
        const score = scoreFrom(yakuResult, fuResult, wc);

        // ⚠️ 高点法：按【点数】取最优 ——
        //    同一手牌可能有多种分解（同一组牌既可作顺子也可作刻子），
        //    番数高者未必点数高（符数不同，如 4番30符 < 3番70符）。
        //    basePoints 与最终支付单调一致（亲/子、自摸/荣和、本场在本局内固定）。
        //    点数相同（如已满贯/跳满，符数不再影响点数）时，取符数高者（与 riichienv 一致）。
        if (
          best === null ||
          score.basePoints > best.score.basePoints ||
          (score.basePoints === best.score.basePoints && fuResult.fu > best.fuResult.fu)
        ) {
          best = { yakuResult, fuResult, score, decomp: d };
        }
      } catch {
        // 单个变体失败不影响其他
      }
    }
  }

  if (!best) return null;

  let { yakuResult, fuResult, score } = best;

  const rules = rulesOf(state);

  // ⚠️ 双倍役满依预设调整（天凤=单倍；雀魂=双倍）→ 以有效倍数重算点数
  if (yakuResult.hasYakuman) {
    const eff = yakuResult.yaku
      .filter((y: Yaku) => y.def.level === 'yakuman' || y.def.level === 'double-yakuman')
      .reduce((n: number, y: Yaku) => n + doubleYakumanMultiplier(y.def.id, rules), 0);
    if (eff > 0 && eff !== yakuResult.yakumanCount) {
      yakuResult = { ...yakuResult, yakumanCount: eff };
      score = scoreFrom(yakuResult, fuResult, winCtx);
    }
  }

  // ── 点数变化 ──
  let changes = computeScoreChanges(state, winner, method, loser, score);

  // ── 包牌（責任払い）覆盖：大三元/大四喜 由鸣牌凑齐 → 责任者分摊基础部分 ──
  //    规则（来源 RiichiEnv docs/RULES.md）：荣和 50/50；自摸责任者全付；
  //    复合役满时 `paoLiabilityOnly`（雀魂）只担包牌役满部分，天凤担全额。
  const paoClaim = yakuResult.hasYakuman
    ? state.pao?.find((c) => c.owner === winner)
    : undefined;
  if (paoClaim && yakuResult.hasYakuman) {
    const paoCount = yakuResult.yaku
      .filter((y: Yaku) => y.def.id === 'daisangen' || y.def.id === 'daisuushii')
      .reduce((n: number, y: Yaku) => n + doubleYakumanMultiplier(y.def.id, rules), 0);
    if (paoCount > 0) {
      const yk = Math.max(1, yakuResult.yakumanCount);
      const paoFrac = Math.min(1, paoCount / yk);
      const nonPaoFrac = 1 - paoFrac;
      const isDealer = winner === dealerSeat(state.round);
      const liable = paoClaim.liable;
      const r100 = (n: number) => Math.round(n / 100) * 100;
      const lim = rules.paoLiabilityOnly; // true=只担包牌部分（雀魂）

      const base: number[] = [0, 0, 0, 0];
      if (method === 'ron') {
        const total = isDealer ? score.dealer.ron : score.nonDealer.ron;
        const paoPortion = lim ? r100(total * paoFrac) : total;
        const half = r100(paoPortion / 2);
        base[winner] += total;
        if (loser !== null && loser !== liable) {
          base[loser] -= total - half; // 非包牌部分 + 包牌的另一半
          base[liable] -= half;
        } else {
          base[liable] -= total; // 责任者即放铳者 → 全付
        }
      } else {
        const perPay = (s: number) =>
          isDealer
            ? score.dealer.tsumoDealer
            : s === dealerSeat(state.round)
              ? score.nonDealer.tsumoDealer
              : score.nonDealer.tsumoNonDealer;
        let total = 0;
        for (let s = 0; s < 4; s++) if (s !== winner) total += perPay(s);
        if (!lim) {
          // 天凤：责任者全额承担，其余不付
          base[winner] += total;
          base[liable] -= total;
        } else {
          // 雀魂：包牌部分全由责任者担，其余部分按常规分摊
          const paoPortion = r100(total * paoFrac);
          for (let s = 0; s < 4; s++) {
            if (s === winner) continue;
            const share = r100(perPay(s) * nonPaoFrac);
            base[s] -= share;
            base[winner] += share;
          }
          base[liable] -= paoPortion;
          base[winner] += paoPortion;
        }
      }
      // 本场 bonus（与常规一致）
      const honba = state.round.honba;
      if (honba > 0) {
        if (method === 'ron' && loser !== null) {
          base[loser] -= honba * 300;
          base[winner] += honba * 300;
        } else {
          for (let s = 0; s < 4; s++) {
            if (s === winner) continue;
            base[s] -= honba * 100;
            base[winner] += honba * 100;
          }
        }
      }
      changes = base;
    }
  }

  return {
    method,
    winner,
    loser,
    winTile: indexToName(tileIndex(winTile)),
    winTileIndex: tileIndex(winTile),
    hand: player.hand
      .filter((t) => t.id !== winTile.id)
      .map((t) => indexToName(tileIndex(t))),
    melds: player.melds.map((m) => m.names.join('') || indexToName(m.start)),
    handTiles: player.hand
      .filter((t) => t.id !== winTile.id)
      .map((t) => tileIndex(t)),
    meldGroups: player.melds.map((m) => ({
      kind: m.kind,
      start: m.start,
      open: m.open,
      calledIndex: m.calledIndex ?? 0,
    })),
    yaku: yakuResult.yaku.map((y) => ({
      name: y.def.name,
      han: y.han, // ⚠️ 用 y.han（考虑副露降番），不是 y.def.han
      yakuman: y.def.level === 'yakuman' || y.def.level === 'double-yakuman',
    })),
    doraHan: yakuResult.doraHan,
    totalHan: yakuResult.hasYakuman ? 0 : yakuResult.totalHan + yakuResult.doraHan,
    // ⚠️ 役满不看符/番：fu 必须置 0，否则 UI 会显示毫无意义的 raw fu（如「40符」）
    fu: yakuResult.hasYakuman ? 0 : fuResult.fu,
    fuDetail: yakuResult.hasYakuman ? '' : fuResult.note ?? '',
    score,
    scoreChanges: changes,
    limitName: yakuResult.hasYakuman
      ? yakuResult.yakumanCount > 1
        ? `役满×${yakuResult.yakumanCount}`
        : '役满'
      : deriveLimitName(best.yakuResult.totalHan + best.yakuResult.doraHan),
    yakuman: yakuResult.hasYakuman,
    yakumanCount: yakuResult.yakumanCount,
  };
}

/**
 * 计算点数变化（含本场 + 供托）
 */
export function computeScoreChanges(
  state: GameState,
  winner: Seat,
  method: 'tsumo' | 'ron',
  loser: Seat | null,
  score: ScoreResult,
): number[] {
  const changes = [0, 0, 0, 0];
  // 庄家 = "自风为东"的座位（由 dealerSeat 决定，随局数轮换）
  // ⚠️ 旧写法 `seatToWind(winner, roundWind) === roundWind` 依赖已废弃的签名/语义，改后恒 false。
  const dealerSeatIdx = dealerSeat(state.round);
  const isDealer = winner === dealerSeatIdx;

  if (method === 'tsumo') {
    if (isDealer) {
      // 亲家自摸：三家各付
      const each = score.dealer.tsumoDealer;
      for (let s = 0; s < 4; s++) {
        if (s === winner) changes[s] += each * 3;
        else changes[s] -= each;
      }
    } else {
      // 子家自摸：庄家付多，闲家付少
      const fromDealer = score.nonDealer.tsumoDealer;
      const fromOthers = score.nonDealer.tsumoNonDealer;
      for (let s = 0; s < 4; s++) {
        if (s === winner) continue;
        if (s === dealerSeatIdx) {
          changes[s] -= fromDealer;
          changes[winner] += fromDealer;
        } else {
          changes[s] -= fromOthers;
          changes[winner] += fromOthers;
        }
      }
    }
  } else {
    const amount = isDealer ? score.dealer.ron : score.nonDealer.ron;
    changes[winner] += amount;
    if (loser !== null) changes[loser] -= amount;
  }

  // 本场 bonus
  const honbaBonus = state.round.honba * 300;
  if (honbaBonus > 0) {
    if (method === 'ron' && loser !== null) {
      changes[loser] -= honbaBonus;
      changes[winner] += honbaBonus;
    } else {
      const per = state.round.honba * 100;
      for (let s = 0; s < 4; s++) {
        if (s === winner) continue;
        changes[s] -= per;
        changes[winner] += per;
      }
    }
  }

  // ⚠️ 供托不在这里加：由 engine 的 settleWinInState 统一处理
  //    （避免重复计算 → 点数不守恒）
  return changes;
}

// ════════════════════════════════════════════════════════════════
// 待ち读法辅助
// ════════════════════════════════════════════════════════════════

/**
 * 枚举和了牌在一个分解下的所有「待ち读法」——
 * 同一张和了牌可能落在不同面子/雀头上（如既可作 345p 的坎张、又可作 456p 的两面，
 * 或为雀头作单骑），天凤/riichienv 按【点数】取高者（高点法）。
 */
function waitReadingsFor(d: Decomposition, w: number): Array<FuWaitType | undefined> {
  const set = new Set<FuWaitType>();
  if (d.pair === w) set.add('tanki');
  for (const m of d.mentsu) {
    if (m.kind !== 'shuntsu' || m.open) continue;
    const startFace = (m.start % 9) + 1;
    if (w === m.start) set.add(startFace === 7 ? 'penchan' : 'ryanmen');
    else if (w === m.start + 1) set.add('kanchan');
    else if (w === m.start + 2) set.add(startFace === 1 ? 'penchan' : 'ryanmen');
  }
  return set.size ? [...set] : [undefined];
}


/** 格式化役种列表 */
export function formatYakuList(detail: WinDetail): string {
  const parts = detail.yaku.map((y) => `${y.name}${y.yakuman ? '(役满)' : `(${y.han}番)`}`);
  if (detail.doraHan > 0) parts.push(`宝牌(${detail.doraHan}番)`);
  return parts.join(' + ') || '（无役）';
}

/** 格式化点数（含亲/子自摸的分配）*/
export function formatScoreDetail(detail: WinDetail): string {
  const { score, method } = detail;
  if (method === 'ron') {
    return `${score.dealer.ron === score.nonDealer.ron ? score.dealer.ron : '?'} 点`;
  }
  // 自摸
  return `${score.nonDealer.tsumoNonDealer}/${score.nonDealer.tsumoDealer} 点`;
}


// ════════════════════════════════════════════════════════════════
// 满贯等级推导
// ════════════════════════════════════════════════════════════════

/**
 * 由番数推导满贯等级名
 *
 * 5番=满贯 / 6-7=跳满 / 8-10=倍满 / 11-12=三倍满 / 13+=累计役满
 */
export function deriveLimitName(han: number): string | null {
  if (han >= 13) return '累计役满';
  if (han >= 11) return '三倍满';
  if (han >= 8) return '倍满';
  if (han >= 6) return '跳满';
  if (han >= 5) return '满贯';
  return null;
}
